import { useEffect, useMemo, useState } from "react";

// Die 6 Standard-Kacheln (LOCKED-Keys) sind IMMER auf dem Kunden-Dashboard sichtbar.
const LOCKED_TILE_KEYS = new Set([
  "last_inspection", "temperature", "water", "heating", "alarm", "internet",
]);
import { Link } from "react-router-dom";
import {
  PlaneLanding, Sparkles, MessageCircle, ClipboardList,
  ClipboardCheck, Hammer, Package, FileText, FileSpreadsheet, Receipt, ChevronRight, Check, CloudOff, CalendarDays, CalendarClock, Home,
} from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useProperty } from "../lib/PropertyContext";
import PropertySwitcher from "../components/customer/PropertySwitcher";
import { ArrivalCountdownWidget } from "../components/dashboard/ArrivalCountdownWidget";
import { ArrivalReadinessBadge } from "../components/dashboard/ArrivalReadinessBadge";
import BookingDetailModal from "../components/BookingDetailModal";
import BookingProgressTimeline from "../components/BookingProgressTimeline";
import CustomerStatusPreview from "../components/CustomerStatusPreview";
import { taskStatusColor, STATUS_COLORS, withAlpha, BRAND_COLORS, effectiveBookingStatus, bookingPill, BOOKING_PILL } from "../lib/bookingStatus";
import { useUnreadMessages } from "../lib/unreadMessages";
import { useUnreadQuotes } from "../lib/unreadQuotes";
import { useUnpaidInvoices } from "../lib/unpaidInvoices";
import { useFeatureFlags } from "../lib/featureFlags";
import { useOnline } from "../lib/offline";
import { useLiveStatusSchema } from "../lib/liveStatusSchema";
import { deDateShort, deDateShortTime, deDayMonth, deDayMonthTime } from "../lib/dateFormat";
import { recommendedPackageForAbsence, packageRenewalDue, recommendedPackageFromStart } from "../lib/servicePackageLimits";

// Feb 2026 · 9 Sep 2026 · User-Directive „Rotierender Cover":
// Zeigt alle Bilder aus property.photos[] mit 5-Sekunden-Fade. Zurück-auf-0
// bei jedem Objekt-Wechsel (`propertyId`-key im useEffect). Auf 1er-Galerien
// wird die Komponente von der Aufrufer-Seite gar nicht gemountet.
function RotatingHero({ photos, name, propertyId }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    if (photos.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), 5000);
    return () => clearInterval(t);
  }, [photos.length, propertyId]);
  return (
    <div className="relative w-full h-full" data-testid="property-hero-rotating">
      {photos.map((fid, i) => (
        <img
          key={`${fid}-${i}`}
          src={fileUrl(fid)}
          alt={name}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms]"
          style={{ opacity: i === idx ? 1 : 0 }}
          data-testid={i === idx ? "property-hero-image" : undefined}
        />
      ))}
    </div>
  );
}

/* ---- Helpers ---- */
// StatusDot + StatusCard leben jetzt in `components/CustomerStatusPreview`
// (shared truth-source für Kunden- und Team-Seite). Hier nur Re-Exports und
// weitere Dashboard-spezifische Helper.

// 24 Jun 2026 · Ganztägige Differenz in Tagen ab heute (lokal). Positiv =
// Zukunft, 0 = heute, negativ = Vergangenheit.
function daysFromToday(iso) {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d - today) / 86400000);
}

function QuickTile({ to, icon: Icon, label, testid, subtle = false, placeholder = false, badge = 0, status = null, accent = null }) {
  const online = useOnline();
  const base = "relative bg-white rounded-xl p-5 flex flex-col gap-4 min-h-[130px] justify-between transition-colors";
  const cls = accent ? `${base} border-2` : `${base} border border-navy/10 hover:border-gold`;
  const accentStyle = accent ? { borderColor: accent } : undefined;
  const inner = (
    <>
      {online === false && !placeholder && (
        <span
          data-testid={`${testid}-offline-badge`}
          className="absolute top-2 right-2 inline-flex items-center text-navy/35"
          title="Offline – lokal gespeicherte Daten"
          aria-label="Offline – lokal gespeicherte Daten"
        >
          <CloudOff className="w-3.5 h-3.5" strokeWidth={1.6} />
        </span>
      )}
      <div className="w-11 h-11 rounded-full bg-ivory flex items-center justify-center relative">
        <Icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
        {badge > 0 && (
          <span
            data-testid={`${testid}-badge`}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-bold text-white flex items-center justify-center leading-none"
            style={{ background: STATUS_COLORS.red, boxShadow: "0 1px 3px rgba(0,0,0,0.25), 0 0 0 1.5px #FFFFFF" }}
            aria-label={`${badge} unread messages`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-end justify-between gap-2">
          <div className={`font-display text-[17px] leading-tight ${subtle ? "text-navy-dark/70" : "text-navy"}`} style={accent ? { color: accent } : undefined}>{label}</div>
          {placeholder && <span className="label-caps text-navy-dark/40 text-[9px]">Soon</span>}
        </div>
        {status && (
          <div className="text-[11px] font-medium text-gold leading-tight" data-testid={`${testid}-status`}>
            {status}
          </div>
        )}
      </div>
    </>
  );
  if (placeholder) return <div className={`${cls} opacity-70 cursor-not-allowed`} style={accentStyle} data-testid={testid}>{inner}</div>;
  return <Link to={to} className={cls} style={accentStyle} data-testid={testid}>{inner}</Link>;
}

function Section({ title, right, children, testid, collapsible = false, defaultCollapsed = false }) {
  const [open, setOpen] = useState(!defaultCollapsed);
  if (!collapsible) {
    return (
      <section className="mt-10" data-testid={testid}>
        <div className="flex items-baseline justify-between mb-3">
          <div className="label-caps">{title}</div>
          {right}
        </div>
        {children}
      </section>
    );
  }
  return (
    <section className="mt-10" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between mb-3 text-left"
      >
        <span className="flex items-center gap-2">
          <ChevronRight
            className={`w-4 h-4 text-navy-dark/50 transition-transform ${open ? "rotate-90" : ""}`}
            strokeWidth={2}
          />
          <span className="label-caps">{title}</span>
        </span>
        {right}
      </button>
      {open && children}
    </section>
  );
}

/* ---- Dashboard ---- */

// 26 Sep 2026 · Servicepaket-Angabe in der Aufenthalts-Kachel. Ein fehlendes
// Paket ist KEIN Fehlerzustand → neutral (navy/gold), nie rot, mit einladendem
// Buchungsweg. Zeigt entweder den gebuchten Paketnamen (rein informativ) oder
// einen CTA zur Buchung. `suggestion` (Paket-ID, aus der Abwesenheitsdauer
// abgeleitet) macht aus dem CTA eine konkrete Empfehlung + Deep-Link.
function StayPackageRow({ pkg, de, suggestion = null }) {
  if (pkg) {
    return (
      <div
        data-testid="upcoming-stay-package"
        className="border-t border-navy/10 px-5 py-2.5 flex items-center gap-2 text-[12.5px] text-navy"
      >
        <Package className="w-3.5 h-3.5 text-gold shrink-0" strokeWidth={1.7} />
        <span className="label-caps text-navy-dark/45">{de ? "Servicepaket" : "Service package"}</span>
        <span className="font-medium truncate">{pkg.package_title}</span>
      </div>
    );
  }
  const to = suggestion ? `/app/service-packages?package=${suggestion}` : "/app/service-packages";
  const label = suggestion
    ? (de ? `Für Ihre Abwesenheit passt Service-Paket ${suggestion}` : `Service Package ${suggestion} fits your absence`)
    : (de ? "Noch kein Servicepaket gebucht" : "No service package booked yet");
  return (
    <Link
      to={to}
      data-testid="upcoming-stay-package-cta"
      data-suggested={suggestion || ""}
      className="border-t border-navy/10 px-5 py-4 flex items-center justify-between gap-3 text-[15px] text-ivory hover:opacity-95 transition-opacity group"
      style={{ background: BRAND_COLORS.navy }}
    >
      <span className="inline-flex items-center gap-2.5 min-w-0">
        <Package className="w-5 h-5 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.7} />
        <span className="truncate font-medium">{label}</span>
      </span>
      <span className="inline-flex items-center gap-1.5 font-semibold shrink-0" style={{ color: BRAND_COLORS.gold }}>
        {de ? "Jetzt buchen" : "Book now"}
        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}

// 27 Sep 2026 · Neutraler Vorlauf-Hinweis vor Paketablauf (Paket 2 = 14 Tage,
// Paket 3 = 3 Tage). Nie rot. Führt zur nahtlosen Verlängerung — Deep-Link mit
// empfohlenem Paket, falls eine Abwesenheit über das Paketende hinausreicht.
function StayPackageRenewalRow({ hint, de }) {
  const to = hint.recommended ? `/app/service-packages?package=${hint.recommended}` : "/app/service-packages";
  const when = hint.daysLeft <= 0
    ? (de ? "heute" : "today")
    : (de ? `in ${hint.daysLeft} ${hint.daysLeft === 1 ? "Tag" : "Tagen"}` : `in ${hint.daysLeft} ${hint.daysLeft === 1 ? "day" : "days"}`);
  return (
    <Link
      to={to}
      data-testid="upcoming-stay-renewal"
      data-days-left={hint.daysLeft}
      className="border-t px-5 py-2.5 flex items-center justify-between gap-2 text-[12.5px] hover:bg-gold/5 transition-colors group"
      style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.35), background: withAlpha(BRAND_COLORS.gold, 0.06) }}
    >
      <span className="inline-flex items-center gap-2 min-w-0">
        <CalendarClock className="w-3.5 h-3.5 text-gold shrink-0" strokeWidth={1.7} />
        <span className="text-navy-dark/75 truncate">
          {de
            ? `${hint.packageTitle} endet ${when} · nahtlos verlängern ab ${deDateShort(hint.seamlessStart)}`
            : `${hint.packageTitle} ends ${when} · renew seamlessly from ${deDateShort(hint.seamlessStart)}`}
        </span>
      </span>
      <span className="inline-flex items-center gap-1 text-gold font-medium shrink-0">
        {de ? "Verlängern" : "Renew"}
        <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const de = lang === "de";
  // Feb 2026 · Rote Flagge auf der Concierge-Kachel: Live-Zähler ungelesener
  // Nachrichten, gepollt aus /me/notifications.
  const { total: unreadMessages } = useUnreadMessages("customer");
  const { total: unreadQuotes } = useUnreadQuotes(user?.role || "customer");
  // 20 Jun 2026 · Rechnungs-Kachel nur, wenn objektbezogene Rechnungen
  // per Admin-Feature-Flag wieder freigegeben sind.
  const { propertyInvoices } = useFeatureFlags();
  const { total: unpaidInvoices } = useUnpaidInvoices(propertyInvoices ? (user?.role || "customer") : null);
  // Feb 2026 · Struktur-Fix Live-Status — die systemweite Master-Liste steuert,
  // welche Kacheln der Kunde sieht (schema-getrieben statt hartcodiert).
  const { items: schema } = useLiveStatusSchema({ team: false });
  // Feb 2026 · 8 Sep 2026 · User-Directive „Objekt-Umschalter global": Der
  // aktive Objekt-State lebt jetzt in `PropertyContext` (siehe
  // `/app/frontend/src/lib/PropertyContext.jsx`). Dashboard bezieht daraus
  // `properties`, `selected` und `setSelectedProperty` — dieselbe Quelle
  // wird auch von `CustomerSettings` (und künftig weiteren Kunden-Seiten)
  // konsumiert, sodass Wechsel überall sofort sichtbar sind.
  const { properties, selected, setSelectedProperty, propertiesById } = useProperty();
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [extras, setExtras] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [openBookingId, setOpenBookingId] = useState(null);
  const [servicePackages, setServicePackages] = useState([]);
  const [reconnectTick, setReconnectTick] = useState(0);

  // 23 Sep 2026 · Auto-Sync beim Wiederverbinden: erhöht den Tick, wodurch die
  // Daten-Effekte unten sofort neu laufen (statt erst beim nächsten 10-s-Poll).
  useEffect(() => {
    const onReconnect = () => setReconnectTick((n) => n + 1);
    window.addEventListener("wb:reconnect", onReconnect);
    return () => window.removeEventListener("wb:reconnect", onReconnect);
  }, []);

  // Objekt-spezifisch gebuchtes Service-Paket — für den Status auf der
  // „Service Pakete"-Kachel (z.B. „Service-Paket 1 gebucht").
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/service-packages/mine");
        if (!cancelled) setServicePackages(data || []);
      } catch (e) { /* Status ist optional */ }
    })();
    return () => { cancelled = true; };
  }, [reconnectTick]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Feb 2026 · 8 Sep 2026 · Properties werden jetzt vom PropertyContext
        // geladen — hier ziehen wir nur die booking/request/message/extras.
        const [b, r, m, e] = await Promise.all([
          api.get("/bookings"),
          api.get("/requests"),
          api.get(`/messages/${user?.id}`),
          api.get("/extras"),
        ]);
        if (cancelled) return;
        setBookings(b.data);
        setRequests(r.data);
        setMessages(m.data);
        setExtras(e.data || []);
      } catch (e) {
        console.error("CustomerDashboard load failed:", e);
      }
    };
    load();
    // Live-Poll (alle 10 s) für Buchungen — Team-Änderungen (z.B. Un-Ticken
    // eines einzigen Punkts flippt den Booking-Status von "completed" zurück
    // auf "in_progress") werden auf dem Home-Screen in Echtzeit sichtbar.
    // Objekte selbst pollt der Context.
    const interval = setInterval(async () => {
      try {
        const b = await api.get("/bookings");
        if (cancelled) return;
        setBookings(b.data);
      } catch (e) { /* silent */ }
    }, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.id, reconnectTick]);

  useEffect(() => {
    if (user?.id && !user.onboarded_at) setShowOnboarding(true);
  }, [user?.id, user?.onboarded_at]);

  const finishOnboarding = async () => {
    setShowOnboarding(false);
    try { await api.post("/me/onboarded"); await refresh?.(); }
    catch (e) { console.debug("onboarded ping failed:", e); }
  };

  const firstName = (user?.first_name || user?.name || user?.email || "").split(" ")[0].split("@")[0];
  const welcomeName = (user?.welcome_name || "").trim();

  // Aufenthalt unter den sechs Kacheln — PRO OBJEKT (selected.id). Laufender
  // Aufenthalt (heute zwischen An- und Abreise) hat Vorrang, sonst der
  // nächste zukünftige. Immer nur EIN Eintrag; stornierte werden ignoriert.
  const currentOrNextStay = useMemo(() => {
    if (!selected?.id) return null;
    const today = new Date().toISOString().slice(0, 10);
    const mine = (bookings || []).filter(
      (b) => b.property_id === selected.id && b.status !== "cancelled" && b.from_date && b.to_date,
    );
    const ongoing = mine
      .filter((b) => b.from_date <= today && today <= b.to_date)
      .sort((a, b) => (a.to_date || "").localeCompare(b.to_date || ""))[0];
    const future = mine
      .filter((b) => b.from_date > today)
      .sort((a, b) => (a.from_date || "").localeCompare(b.from_date || ""))[0];
    const pick = ongoing ? { booking: ongoing, ongoing: true }
      : future ? { booking: future, ongoing: false }
      : null;
    if (!pick) return null;
    // Dezenter Countdown „in X Tagen" (nächster) bzw. „noch X Tage" (laufend),
    // damit sich der Aufenthalt sofort einordnen lässt.
    const n = daysFromToday(pick.ongoing ? pick.booking.to_date : pick.booking.from_date);
    if (n === null) {
      pick.countdown = null;
    } else if (pick.ongoing) {
      pick.countdown = n <= 0 ? (de ? "endet heute" : "ends today")
        : n === 1 ? (de ? "noch 1 Tag" : "1 day left")
        : (de ? `noch ${n} Tage` : `${n} days left`);
    } else {
      pick.countdown = n <= 0 ? (de ? "heute" : "today")
        : n === 1 ? (de ? "morgen" : "tomorrow")
        : (de ? `in ${n} Tagen` : `in ${n} days`);
    }
    return pick;
  }, [bookings, selected?.id, de]);

  // Feb 2026 · 8 Sep 2026 · `propertiesById` kommt aus dem PropertyContext —
  // keine lokale Kopie mehr nötig.

  const recentActivity = useMemo(() => {
    // Feb 2026 · Merge requests + messages und schreibe pro Item einen
    // effektiven Status, damit das Icon-Kreis-Farbschema (Grau/Blau/Grün/Rot)
    // strikt dem Task-Status folgt. Nachrichten sind keine Aufgabe → sie
    // bleiben neutral (grau), damit sie in der Liste nicht wie ein offener
    // Task aussehen.
    // Feb 2026 · 8 Sep 2026 · User-Directive „Buchungshistorie fuer Gast":
    // Kunden UND Gaeste (via Datenerhalt-Regel nach Downgrade) sollen ihre
    // abgeschlossenen/stornierten Buchungen historisch sehen. Dashboard
    // „Zuletzt passiert" ist der leichtgewichtige Traeger dafuer, weil er
    // die Chronik-Semantik bereits nutzt (statt eine neue Historie-Sektion
    // aufzumachen). Wir zeigen nur wirklich abgeschlossene Buchungen —
    // aktive/zukuenftige stehen weiter in „Kommende Abwesenheiten".
    // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter: Requests + Buchungen werden
    // auf das aktive Objekt eingegrenzt. Nachrichten sind pro Nutzer, nicht
    // pro Objekt → keine Filterung dort.
    const propId = selected?.id;
    const today = new Date().toISOString().slice(0, 10);
    const scopedRequests = requests.filter((r) => (propId ? r.property_id === propId : true));
    const pastBookings = (bookings || []).filter((b) => {
      if (propId && b.property_id !== propId) return false;
      const finished = b.status === "completed" || b.status === "cancelled";
      const passed = (b.to_date || "") && b.to_date < today;
      return finished && passed;
    });
    const items = [
      ...scopedRequests.map((r) => ({
        id: `r-${r.id}`, kind: de ? "Service" : "Service", title: r.title, at: r.created_at, icon: Hammer,
        status: r.status || "requested",
        urgent: r.priority === "urgent",
        done: r.status === "completed" || r.status === "cancelled",
      })),
      ...messages.slice(-8).map((m) => ({
        id: `m-${m.id}`, kind: de ? "Nachricht" : "Message", title: m.text || (de ? "[Anhang]" : "[Attachment]"),
        at: m.created_at, icon: MessageCircle,
        status: "message", // neutral · greift auf grey-Default zurück
      })),
      ...pastBookings.map((b) => {
        const propName = (propertiesById[b.property_id] || {}).name || "";
        const from = b.from_date
          ? deDateShort(b.from_date)
          : "";
        const to = b.to_date
          ? deDateShort(b.to_date)
          : "";
        const range = from && to ? `${from} → ${to}` : (b.from_date || "");
        const title = propName ? `${range} · ${propName}` : range;
        return {
          id: `b-${b.id}`,
          kind: de ? "Abwesenheit" : "Absence",
          title,
          at: b.to_date || b.from_date || b.created_at,
          icon: PlaneLanding,
          status: b.status,
          done: true, // Historie ist per Definition erledigt (completed/cancelled)
          cancelled: b.status === "cancelled",
        };
      }),
    ];
    return items
      .filter((x) => x.at)
      .sort((a, b) => (b.at || "").localeCompare(a.at || ""))
      .slice(0, 8);
  }, [requests, messages, bookings, propertiesById, de, selected?.id]);

  // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter: Buchungen des aktiven Objekts
  // für die Ankunfts-Widgets (Countdown + Readiness-Badge) einmalig ableiten,
  // damit die Widgets nur Ereignisse für das aktive Objekt anzeigen.
  const scopedBookings = useMemo(() => {
    const propId = selected?.id;
    if (!propId) return bookings;
    return bookings.filter((b) => b.property_id === propId);
  }, [bookings, selected?.id]);

  // 26 Sep 2026 · Das für das aktive Objekt gebuchte Servicepaket (der
  // angezeigten Abwesenheit zugeordnet). Pakete werden pro Objekt gebucht,
  // nicht pro einzelner Abwesenheit → wir nehmen das Paket des Objekts.
  const bookedPackage = useMemo(() => {
    const list = servicePackages || [];
    if (!list.length) return null;
    return (selected?.id ? list.find((x) => x.property_id === selected.id) : list[0]) || null;
  }, [servicePackages, selected?.id]);

  // 27 Sep 2026 · Fällige Verlängerung (neutraler In-App-Hinweis in der
  // Aufenthalts-/Servicepaket-Kachel). Empfehlung aus einer Abwesenheit, die
  // über das Paketende hinausreicht (kürzestes ausreichendes Paket).
  const renewalHint = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const due = packageRenewalDue(servicePackages, selected?.id, todayIso);
    if (!due) return null;
    // 27 Sep 2026 · Vorwarnung NUR, wenn für dieses Objekt noch eine zukünftige
    // Abwesenheit Deckung über das Paketende hinaus braucht. Sonst kein Hinweis.
    const beyond = (bookings || [])
      .filter((b) => b.property_id === selected?.id && b.status !== "cancelled" && b.to_date && b.to_date > due.endDate)
      .map((b) => b.to_date)
      .sort();
    if (!beyond.length) return null;
    const recommended = recommendedPackageFromStart(due.seamlessStart, beyond[beyond.length - 1]);
    return { ...due, recommended };
  }, [servicePackages, bookings, selected?.id]);

  const lastUpdatedAt = useMemo(() => {
    const ts = selected?.status_updated_at || selected?.updated_at || selected?.last_inspection;
    if (!ts) return null;
    try {
      return typeof ts === "string" && ts.length === 10 ? deDayMonth(ts) : deDayMonthTime(ts);
    } catch { return ts; }
  }, [selected]);

  const statusLabel = selected?.status === "attention" ? (de ? "Aufmerksam" : "Attention") : selected?.status === "emergency" ? (de ? "Notfall" : "Emergency") : (de ? "Bereit" : "Ready");
  // statusIsReady steuert nur noch den Farbton der Property-Hero-Pill; die
  // Haupt-Überschrift verwendet ausschließlich `allTilesOk` (siehe unten),
  // damit sie sich strikt an die sechs Status-Kacheln koppelt.
  const statusIsReady = !selected?.status || selected.status === "ready";

  // Alle sechs Status-Kacheln müssen grün sein, damit die Haupt-Überschrift
  // "Ihr Haus ist bereit …" angezeigt wird. Sobald auch nur eine Kachel auf
  // Handlungsbedarf umschlägt, wird die komplette Begrüßungs-Sektion (Label
  // + h1 + Status-Zeile) ausgeblendet, damit der Kunde nicht in falscher
  // Sicherheit gewiegt wird. Der Poll (alle 30 s) triggert die Re-Evaluation
  // sofort, sobald das Team einen Mangel in der Live-Status-Liste markiert.
  // Feb 2026 · HARDCODED-RULE — die 6 Standard-Kacheln (LOCKED-Keys) sind
  // IMMER auf dem Kunden-Dashboard sichtbar. Deshalb muss auch das Greeting
  // strikt an diesen 6 Kacheln hängen, egal was in `dashboard_tile_keys`
  // steht (Legacy-Property-Docs konnten ein LOCKED-Item durch ein Custom-
  // Item ersetzen — jetzt gilt: 6 LOCKED-Keys = Truth-Source).
  const allTilesOk = useMemo(() => {
    if (!selected) return false;
    for (const key of LOCKED_TILE_KEYS) {
      const sev = selected[`${key}_severity`];
      if (sev && sev !== "ok") return false;
    }
    return true;
  }, [selected]);

  // Feb 2026 directive — synchronise the greeting headline with the booking
  // status. If ANY active booking for the currently-selected property is
  // still in the concierge team's hands (i.e. status is neither `completed`
  // nor `cancelled` AND its arrival day is still today or in the future),
  // the customer sees „Status-Update für Ihr Objekt" INSTEAD of the
  // „Ihr Haus ist bereit …" line. The backend now recomputes booking status
  // on EVERY GET (see `verify_and_correct_booking_status`), so removing a
  // single Team-Haken flips completed → in_progress → the next 10 s poll
  // updates `bookings` → this memo re-runs → the headline swaps in real time
  // WITHOUT a page reload.
  const hasActiveOngoingBooking = useMemo(() => {
    if (!selected) return false;
    const today = new Date().toISOString().slice(0, 10);
    return bookings.some((b) => (
      b.property_id === selected.id
      && b.status !== "cancelled"
      && b.status !== "completed"
      && (b.to_date || "") >= today
    ));
  }, [bookings, selected]);

  // Show the greeting block whenever we still have a message to render:
  //   • no property set up yet  → setup line
  //   • active booking pending  → „Status-Update für Ihr Objekt"
  //   • all tiles ok            → readiness line
  // Only when we're between bookings AND a tile is red do we hide it, so
  // the red property hero pill can command the customer's attention alone.
  const greetingVisible = !selected || hasActiveOngoingBooking || allTilesOk;

  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-10 py-8 md:py-14 pb-32" data-testid="customer-dashboard">

      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.8)}` }} data-testid="customer-onboarding-tour">
          <div className="bg-ivory max-w-md w-full border border-gold/40 p-8">
            <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>
              {de ? "Willkommen bei Weissblom" : "Welcome to Weissblom"}
            </div>
            <h2 className="font-display text-3xl text-navy mb-3">
              {de ? "Guten Tag, " : "Hello, "}<span className="italic">{firstName || (de ? "Willkommen" : "Welcome")}</span>
            </h2>
            <p className="text-[14px] text-navy-dark/70 leading-relaxed mb-6">
              {de
                ? <>Alles, was Ihr Zuhause braucht — an einem Ort. Über das <span className="text-navy">+</span> in der Leiste erreichen Sie unser Team jederzeit.</>
                : <>Everything your home needs — in one place. Reach our team any time through the <span className="text-navy">+</span> in the bar.</>}
            </p>
            <button data-testid="customer-onboarding-finish-btn" onClick={finishOnboarding}
              className="w-full text-ivory py-3 text-[12px] tracking-[0.15em] uppercase" style={{ background: BRAND_COLORS.navy }}>
              {de ? "Los geht's →" : "Let's begin →"}
            </button>
          </div>
        </div>
      )}

      {/* Greeting — three states (Feb 2026, sync with booking status):
          • no property set up yet          → setup line
          • active booking still pending    → „Status-Update für Ihr Objekt"
          • all bookings done + tiles green → „Ihr Haus ist bereit …"
          The block only hides when we're between bookings AND a tile is red,
          so the red property hero pill can command attention on its own. */}
      {greetingVisible && (
        <div className="mb-6" data-testid="dashboard-greeting-block">
          <div className="label-caps mb-3 text-center">{de ? "Sie buchen. Weissblom liefert." : "You ask. Weissblom delivers."}</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy leading-tight" data-testid="dashboard-greeting">
            {de ? "Hallo, " : "Hello, "}<span className="italic">{welcomeName || firstName || (de ? "Willkommen" : "Welcome")}</span>
          </h1>
          <p
            className="text-[15px] text-navy-dark/70 mt-3"
            data-testid="dashboard-status-line"
            data-status-line-mode={
              !selected ? "setup" : hasActiveOngoingBooking ? "in-progress" : "ready"
            }
          >
            {selected
              ? (hasActiveOngoingBooking
                ? (de ? "Status-Update für Ihr Objekt" : "Status update for your property")
                : (de ? "Ihr Haus ist bereit. Alle Systeme in Ordnung." : "Your house is ready. All systems fine."))
              : (de ? "Ihr Concierge-Team richtet Ihr Zuhause gerade ein." : "Your concierge team is setting up your home.")}
          </p>
        </div>
      )}

      {/* Feb 2026 · 8 Sep 2026 · Objekt-Umschalter aus wiederverwendbarer
          Component (auch in CustomerSettings). Rendert nichts bei Ein-Objekt-
          Kunden — Multi-Objekt-Nutzer bekommen synchronisierte Tab-Bar. */}
      <PropertySwitcher />

      {/* Property card with hero image + status pill overlay */}
      {selected && (
        <div className="relative overflow-hidden bg-navy/5 border border-navy/10" data-testid="property-hero-card">
          <div className="aspect-[16/10] md:aspect-[21/9] relative">
            {(() => {
              // 20 Jun 2026 · User-Directive Punkt 11: Das große Objektbild
              // stammt AUSSCHLIESSLICH aus der Kunden-Galerie (property.photos[]).
              // Mehrere Bilder → rotierender Cover (5s). Genau eines → dieses.
              // Nur wenn der Kunde KEIN Bild hat: Fallback auf das
              // Concierge-Bild (property.photo_url). Sonst Verlauf/Platzhalter.
              // user.hero_image_url wird hier bewusst NICHT mehr gelesen.
              const gallery = Array.isArray(selected.photos) ? selected.photos.filter(Boolean) : [];
              if (gallery.length >= 2) {
                return <RotatingHero photos={gallery} name={selected.name} propertyId={selected.id} />;
              }
              if (gallery.length === 1) {
                return <img src={fileUrl(gallery[0])} alt={selected.name} className="w-full h-full object-cover" data-testid="property-hero-image" />;
              }
              if (selected.photo_url) {
                return <img src={fileUrl(selected.photo_url)} alt={selected.name} className="w-full h-full object-cover" data-testid="property-hero-image" />;
              }
              return <div className="w-full h-full bg-gradient-to-br from-navy/80 to-navy" />;
            })()}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-3">
              <div className="text-ivory">
                {selected.city && <div className="label-caps text-ivory/80 mb-1">{selected.city}</div>}
                <div className="font-display text-2xl md:text-3xl leading-tight" data-testid="property-name">{selected.name}</div>
                <div className="text-[12px] md:text-[13px] text-ivory/85 mt-1">
                  {selected.address}{selected.city ? `, ${selected.city}` : ""}
                </div>
              </div>
              <span
                data-testid="house-status-pill"
                className="inline-flex items-center gap-1.5 bg-ivory/95 text-navy px-3 py-1 text-[11px] font-medium rounded-full shrink-0"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusIsReady ? STATUS_COLORS.green : BRAND_COLORS.gold }} />
                {statusLabel}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Status grid (2 columns on mobile) */}
      {selected && (
        <>
          <CustomerStatusPreview property={selected} schema={schema} />
          {lastUpdatedAt && (
            <div className="text-[11.5px] text-navy-dark/55 italic mt-3 px-1">
              {de ? "Letzte Aktualisierung: " : "Last updated: "}{lastUpdatedAt}
            </div>
          )}
          {/* Aktuelle/Nächste Abwesenheit · ursprüngliche Kachel mit voller
              Statuslogik (Status-Pille · Objekt/Extras-Zeile · 3-Schritt-
              Fortschritt · Farbstreifen links), anklickbar → öffnet die
              Buchung. Position unverändert (direkt unter „Letzte
              Aktualisierung", über An-/Abreise + Servicepakete). */}
          <div className="mt-5">
            {currentOrNextStay ? (() => {
              const b = currentOrNextStay.booking;
              const ongoing = currentOrNextStay.ongoing;
              const propName = propertiesById[b.property_id]?.name || selected?.name || "";
              const extrasCount = (b.services || []).length;
              const dep = b.departure_checklist || [];
              const depDone = dep.filter((x) => x.done).length;
              const depAll = dep.length;
              // Display-Status (Kundenansicht): eine LAUFENDE Abwesenheit ist
              // immer BLAU „In Betreuung" — sie kann nie „Erledigt"/grün sein,
              // solange der Zeitraum noch läuft, auch wenn im Backend bereits
              // alle Checklisten abgehakt wurden.
              const effStatus = effectiveBookingStatus(b);
              const basePill = bookingPill(b);
              const pill = ongoing
                ? { label: de ? "In Betreuung" : "In service", bg: STATUS_COLORS.blue, fg: "#F7F3EC" }
                : { label: de ? basePill.label : (BOOKING_PILL[effStatus]?.label_en || basePill.label), bg: basePill.bg, fg: basePill.fg };
              // Zeitstrahl: bei laufender Abwesenheit als „in Betreuung"
              // behandeln, damit der aktuelle Schritt blau statt grau ist. Das
              // `dateAware`-Flag sorgt dafür, dass Schritte mit Datum in der
              // Zukunft NICHT als erledigt gezeigt werden.
              const timelineBooking = ongoing ? { ...b, status: "in_progress" } : b;
              const showTimeline = effStatus !== "requested" && effStatus !== "cancelled";
              return (
                <div
                  className="relative w-full bg-white border border-navy/10 hover:border-gold hover:shadow-sm transition-colors overflow-hidden"
                  data-testid="upcoming-stay-card"
                >
                  <span
                    aria-hidden="true"
                    data-testid="upcoming-stay-stripe"
                    className="absolute left-0 top-0 bottom-0 w-1 z-10"
                    style={{ background: pill.bg }}
                  />
                  <button
                    type="button"
                    onClick={() => setOpenBookingId(b.id)}
                    data-testid="upcoming-stay-tile"
                    data-stay-ongoing={ongoing ? "true" : "false"}
                    data-stay-status={ongoing ? "in_service" : effStatus}
                    className="w-full flex flex-col text-left"
                  >
                    <div className="w-full px-5 py-3.5 flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="label-caps text-navy-dark/50 mb-0.5">
                          {ongoing
                            ? (de ? "Aktuelle Abwesenheit" : "Current absence")
                            : (de ? "Ihre nächste Abwesenheit" : "Your next absence")}
                        </div>
                        <div className="font-display text-lg text-navy leading-tight" data-testid="upcoming-stay-dates">
                          {deDateShort(b.from_date)}
                          <span className="text-navy/40 mx-1.5">→</span>
                          {deDateShort(b.to_date)}
                        </div>
                        <div className="text-[12px] text-navy-dark/60 mt-1 flex flex-wrap items-center gap-x-2">
                          <span>{de ? "Abwesenheit" : "Absence"}</span>
                          {propName && <span>· {propName}</span>}
                          {extrasCount > 0 && <span>· {extrasCount} {de ? (`Extra${extrasCount === 1 ? "" : "s"}`) : (`extra${extrasCount === 1 ? "" : "s"}`)}</span>}
                          {depAll > 0 && <span>· {de ? "Checkliste" : "Checklist"} {depDone}/{depAll}</span>}
                          {bookedPackage
                            ? <span className="inline-flex items-center gap-1" data-testid="upcoming-stay-package-inline">·<Package className="w-3 h-3 text-gold" strokeWidth={1.8} />{bookedPackage.package_title}</span>
                            : <span className="text-navy-dark/40" data-testid="upcoming-stay-package-inline-none">· {de ? "Kein Servicepaket" : "No service package"}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className="inline-flex items-center px-2.5 py-1 text-[10.5px] tracking-[0.14em] uppercase rounded-full"
                          style={{ background: pill.bg, color: pill.fg }}
                          data-testid="upcoming-stay-status"
                        >
                          {pill.label}
                        </span>
                        <ChevronRight className="w-4 h-4 text-navy-dark/40" />
                      </div>
                    </div>
                    {showTimeline && (
                      <div
                        className="w-full px-4 pb-4 pt-1"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="upcoming-stay-timeline"
                      >
                        <BookingProgressTimeline
                          booking={timelineBooking}
                          property={propertiesById[b.property_id]}
                          variant="customer"
                          compact
                          dateAware
                        />
                      </div>
                    )}
                  </button>
                  {/* Fehlendes Paket → einladender CTA (neutral, nie rot) mit
                      konkretem Paketvorschlag aus der Abwesenheitsdauer. Ist ein
                      Paket gebucht, steht der Name bereits inline in der Meta-Zeile. */}
                  {!bookedPackage && <StayPackageRow pkg={null} de={de} suggestion={recommendedPackageForAbsence(b.from_date, b.to_date)} />}
                  {renewalHint && <StayPackageRenewalRow hint={renewalHint} de={de} />}
                </div>
              );
            })() : (
              /* Kachel IMMER sichtbar — auch ohne Abwesenheit: neutraler Hinweis
                 + dieselbe Servicepaket-Fußzeile (Paket ja/nein auf einen Blick). */
              <div
                className="w-full bg-white border border-navy/10 overflow-hidden"
                data-testid="upcoming-stay-empty"
              >
                <div className="px-5 py-3.5 text-[13px] text-navy-dark/55 flex items-center gap-3">
                  <CalendarDays className="w-4 h-4 text-navy-dark/30 shrink-0" strokeWidth={1.6} />
                  {de ? "Derzeit keine Abwesenheit gebucht" : "No absence booked at the moment"}
                </div>
                <StayPackageRow pkg={bookedPackage} de={de} />
                {renewalHint && <StayPackageRenewalRow hint={renewalHint} de={de} />}
              </div>
            )}
          </div>

          {/* Position 5 · „Verreisen leicht gemacht" — vollbreite, präsente
              Kachel. Gold umrandet + goldene Überschrift; Fläche bleibt hell.
              Gold ist hier KEINE Statusfarbe. Servicepakete + Sonderwünsche
              sind jetzt Kacheln im Schnellzugriff-Achterblock unten. */}
          <div className="mt-6">
            <Link
              to="/app/arrival"
              data-testid="quick-arrival"
              className="w-full bg-white border-2 hover:shadow-md transition-shadow flex items-center justify-between gap-4 px-6 py-6 md:py-7"
              style={{ borderColor: BRAND_COLORS.gold }}
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: withAlpha(BRAND_COLORS.gold, 0.14) }}>
                  <PlaneLanding className="w-5 h-5 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.7} />
                </div>
                <div className="min-w-0">
                  <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{de ? "Verreisen leicht gemacht" : "Travelling made easy"}</div>
                  <div className="font-display text-xl md:text-2xl text-navy leading-tight truncate">
                    {de ? "Meine Abwesenheit vorbereiten" : "Prepare my absence"}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 shrink-0" style={{ color: BRAND_COLORS.gold }} />
            </Link>
          </div>
        </>
      )}

      {/* Ankunfts-Readiness — 24-h-Fenster: kleines Countdown-Widget das
          entweder GRÜN „Alles bereit für Sie" oder ROT „Team ist noch dran"
          zeigt. Farbe folgt strikt der Kalender-Ankunfts-Badge (offene
          Checklist-Items = rot, alles abgehakt = grün). Rendert nur wenn
          `to_date` innerhalb der nächsten 24 h liegt. */}
      <ArrivalReadinessBadge bookings={scopedBookings} propertiesById={propertiesById} />

      {/* Anreise-Countdown (nur wenn Rückreise in ≤ 7 Tagen) */}
      <ArrivalCountdownWidget bookings={scopedBookings} propertiesById={propertiesById} />

      {/* Überblick */}
      <Section title={<span className="font-bold">{de ? "Überblick" : "Overview"}</span>} testid="quick-access">
        <div className="grid grid-cols-2 gap-3">
          {/* Achter-Block (User-Directive 27 Sep 2026), 2-spaltig links→rechts:
              R1 Servicepakete · Sonderwünsche | R2 Ihre Anfragen · Leistungen
              | R3 Wochenberichte · Dokumente | R4 Meine Rechnungen ·
              Kostenvoranschläge. Servicepakete + Sonderwünsche sind hierher
              gewandert und nutzen dieselbe Kachel-Komponente wie die übrigen. */}
          <QuickTile to="/app/service-packages" icon={Package} label={de ? "Servicepakete" : "Service packages"} testid="quick-service-packages" />
          <QuickTile to="/app/request-new" icon={Sparkles} label={de ? "Sonderwünsche" : "Request Anything"} testid="quick-request-anything" accent={BRAND_COLORS.navy} />
          <QuickTile to="/app/requests" icon={ClipboardList} label={de ? "Ihre Anfragen auf einen Blick" : "Your requests at a glance"} testid="quick-requests-overview" />
          <QuickTile to="/app/catalogue" icon={Home} label={de ? "Leistungsübersicht" : "Services overview"} testid="quick-service" />
          <QuickTile to="/app/updates" icon={ClipboardCheck} label={t.nav.updates} testid="quick-inspection" />
          <QuickTile to="/app/documents" icon={FileText} label={de ? "Dokumente" : "Documents"} testid="quick-documents" />
          <QuickTile to="/app/sevdesk-invoices" icon={Receipt} label={de ? "Meine Rechnungen" : "My Invoices"} testid="quick-sevdesk-invoices" />
          <QuickTile to="/app/quotes" icon={FileSpreadsheet} label={de ? "Kostenvoranschläge" : "Quotes"} testid="quick-quotes" badge={unreadQuotes} />
          {propertyInvoices && (
            <QuickTile to="/app/invoices" icon={Receipt} label={de ? "Rechnungen" : "Invoices"} testid="quick-invoices" badge={unpaidInvoices} />
          )}
        </div>
      </Section>

      {/* Booking detail modal */}
      {openBookingId && (
        <BookingDetailModal
          booking={bookings.find((b) => b.id === openBookingId)}
          extras={extras}
          propertyName={propertiesById[bookings.find((b) => b.id === openBookingId)?.property_id]?.name}
          onClose={() => setOpenBookingId(null)}
          onChanged={async () => {
            try {
              const { data } = await api.get("/bookings");
              setBookings(data);
            } catch (e) { /* silent */ }
          }}
        />
      )}

      {/* Zuletzt passiert — standardmäßig eingeklappt (User-Directive 20 Jun 2026) */}
      <Section title={de ? "Zuletzt passiert" : "Recent activity"} testid="recent-activity-section" collapsible defaultCollapsed>
        {recentActivity.length === 0 ? (
          <div className="text-[13px] text-navy-dark/50">—</div>
        ) : (
          <ul className="space-y-3" data-testid="recent-activity-list">
            {recentActivity.map((r) => {
              // Feb 2026 · Icon-Kreis-Farbe folgt strikt dem Task-Status.
              // Dringende offene Anfragen (urgent) werden ROT — auch wenn
              // ihr `status` noch „requested/in_progress" ist. Erledigte
              // Requests erzwingen die grüne Farbe unabhängig vom
              // Textstatus, damit die Liste konsistent bleibt.
              const derived = r.done ? "completed" : r.status;
              const { bg, fg } = taskStatusColor(derived, { urgent: r.urgent });
              const Icon = r.done ? Check : r.icon;
              return (
                <li
                  key={r.id}
                  // Feb 2026 · User-Wunsch: keine sichtbaren Verbindungslinien
                  // zwischen den Karten. Der frühere 1-px-Rahmen an jeder
                  // Karte lief bei gestapelten Items optisch zu einer
                  // durchgehenden Vertikal-Linie zusammen. Karten sind jetzt
                  // randlos mit weichem Inline-Schatten (`--radius` wird im
                  // Tailwind-Setup überschrieben, deshalb Inline-Styles
                  // hier, damit die Abrundung + der Schatten wirklich
                  // greifen).
                  className={`bg-white p-4 flex items-center gap-3 transition-opacity ${r.done ? "opacity-60" : ""}`}
                  style={{
                    borderRadius: "10px",
                    boxShadow: `0 1px 3px ${withAlpha(BRAND_COLORS.navy, 0.07)}, 0 1px 2px ${withAlpha(BRAND_COLORS.navy, 0.04)}`,
                  }}
                  data-testid={`recent-activity-item-${r.id}${r.done ? "-done" : ""}`}
                  data-status={derived}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: bg }}
                    aria-label={`Status: ${derived}`}
                  >
                    <Icon
                      className="w-4 h-4"
                      strokeWidth={r.done ? 3 : 1.9}
                      style={{ color: fg }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[14px] font-medium truncate ${r.done ? "text-navy-dark/60 line-through decoration-navy-dark/25" : "text-navy"}`}>
                      {r.title || "—"}
                    </div>
                    <div className="text-[11.5px] text-navy-dark/55 mt-0.5">
                      {deDateShortTime(r.at)} · {r.kind}
                      {r.done && <span className="ml-1.5" style={{ color: STATUS_COLORS.green }}>· {de ? "erledigt" : "done"}</span>}
                      {r.urgent && !r.done && <span className="ml-1.5" style={{ color: STATUS_COLORS.red }}>· {de ? "dringend" : "urgent"}</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}
